import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Divider,
  Button,
} from '@mui/material';
import { AccessTime, LocationOn, Person, Camera, TaskAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { NotificationData } from '../types/notification';
import ImageWithDetectionBox from './ImageWithDetectionBox';
import { formatActionTypeLabel } from '../utils/formatActionType';

interface NotificationDetailModalProps {
  open: boolean;
  onClose: () => void;
  notification: NotificationData | null;
  onAcknowledge?: (notification: NotificationData, event?: React.MouseEvent) => void;
  onResolve?: (notification: NotificationData, event?: React.MouseEvent) => void;
}

const NotificationDetailModal: React.FC<NotificationDetailModalProps> = ({
  open,
  onClose,
  notification,
  onAcknowledge,
  onResolve,
}) => {
  const { t } = useTranslation('components', { keyPrefix: 'notificationDetailModal' });

  if (!notification) return null;

  const formatWorkflowDateTime = (value: string | null) => {
    if (!value) return '';

    const companyLocalValue = value
      .trim()
      .replace(' ', 'T')
      .replace(/(?:Z|[+-]\d{2}:?\d{2})$/i, '')
      .replace(/(\.\d{3})\d+$/, '$1');
    const parsedDate = new Date(companyLocalValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return value;
    }

    return parsedDate.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const getAlertLevelColor = (level: string | null) => {
    switch (level) {
      case 'red':
        return 'error';
      case 'yellow':
        return 'warning';
      case 'green':
        return 'success';
      default:
        return 'default';
    }
  };

  const statusLabel = t(`status.${notification.status}`, { defaultValue: notification.status });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          background: 'var(--an-primary)',
          color: 'var(--an-white)',
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('title')}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
            {notification.title || notification.policyName}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'var(--an-white)',
            '&:hover': {
              backgroundColor: 'rgb(var(--an-surface-rgb) / 0.10)',
            },
          }}
        >
          <i className="bi bi-x-lg"></i>
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3, bgcolor: 'var(--an-surface)' }}>
        {/* Alert Level Badge */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
          <Chip
            label={`${t('alertLevel')} ${notification.alertLevel?.toUpperCase() ?? t('infoFallback')}`}
            color={getAlertLevelColor(notification.alertLevel) as any}
            sx={{ fontWeight: 600, px: 1 }}
          />
          <Chip
            label={statusLabel}
            sx={{ fontWeight: 600, px: 1, ml: 1 }}
          />
        </Box>
        {notification.message && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1">{notification.message}</Typography>
          </Box>
        )}

        {/* Detection Image */}
        <Box
          sx={{
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden',
            border: '2px solid',
            borderColor: 'divider',
            backgroundColor: 'var(--an-black)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {notification.imageUrl && notification.coordinates ? (
            <ImageWithDetectionBox
              src={notification.imageUrl}
              alt={t('detectionAlt', { employeeName: notification.employeeName })}
              coordinates={notification.coordinates}
              className="img-fluid"
              style={{
                maxWidth: '100%',
                maxHeight: '500px',
                objectFit: 'contain',
              }}
              showDetectionBox={false}
              showEmployeeName={true}
              employeeName={notification.employeeName}
            />
          ) : (
            <img
              src={notification.imageUrl}
              alt={t('detectionAlt', { employeeName: notification.employeeName })}
              style={{
                maxWidth: '100%',
                maxHeight: '500px',
                objectFit: 'contain',
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const errorMsg = document.createElement('div');
                errorMsg.style.padding = '40px';
                errorMsg.style.color = 'var(--an-text-tertiary)';
                errorMsg.style.textAlign = 'center';
                errorMsg.textContent = t('imageNotAvailable');
                target.parentElement?.appendChild(errorMsg);
              }}
            />
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Detection Details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Person sx={{ color: 'primary.main', mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('employee')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {notification.employeeName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('employeeIdPrefix')} {notification.employeeId}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <LocationOn sx={{ color: 'primary.main', mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('department')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {notification.department}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Camera sx={{ color: 'primary.main', mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('camera')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {notification.camera}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <AccessTime sx={{ color: 'primary.main', mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('detectionTime')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {notification.detectionTime}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <TaskAlt sx={{ color: 'primary.main', mt: 0.3 }} />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {t('actionType')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatActionTypeLabel(notification.actionType)}
                  </Typography>
                </Box>
              </Box>
            </Box>

          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('workflow')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {`${t('status.unread')} -> ${t('status.read')} -> ${t('status.acknowledged')} -> ${t('status.resolved')}`}
          </Typography>
          {notification.acknowledgedAt && (
            <Typography variant="body2">
              <strong>{t('acknowledgedAt')}:</strong> {formatWorkflowDateTime(notification.acknowledgedAt)}
            </Typography>
          )}
          {notification.acknowledgedBy && (
            <Typography variant="body2">
              <strong>{t('acknowledgedBy')}:</strong> {notification.acknowledgedBy}
            </Typography>
          )}
          {notification.resolvedAt && (
            <Typography variant="body2">
              <strong>{t('resolvedAt')}:</strong> {formatWorkflowDateTime(notification.resolvedAt)}
            </Typography>
          )}
          {notification.resolvedBy && (
            <Typography variant="body2">
              <strong>{t('resolvedBy')}:</strong> {notification.resolvedBy}
            </Typography>
          )}
          {notification.resolutionNote && (
            <Typography variant="body2">
              <strong>{t('resolutionNote')}:</strong> {notification.resolutionNote}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {onAcknowledge && notification.status !== 'acknowledged' && notification.status !== 'resolved' && (
              <Button variant="outlined" startIcon={<i className="bi bi-check-lg"></i>} onClick={(event) => onAcknowledge(notification, event)}>
                {t('acknowledge')}
              </Button>
            )}
            {onResolve && notification.status !== 'resolved' && (
              <Button variant="contained" startIcon={<i className="bi bi-check2-circle"></i>} onClick={(event) => onResolve(notification, event)}>
                {t('resolve')}
              </Button>
            )}
          </Box>
        </Box>

        {/* Additional Info Box */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 1,
            backgroundColor: 'rgb(var(--an-primary-rgb) / 0.08)',
            border: '1px solid',
            borderColor: 'primary.main',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>{t('policyLabel')}</strong> {t('policyExplanation', { policyName: notification.policyName })}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationDetailModal;

